import { useRef } from 'react';
import { useWheelNavigation } from '../hooks/useWheelNavigation';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatDate } from '../lib/dateUtils';

export const CashFlowView = ({ config, handleItemSelect, selectedIndex, setSelectedIndex }: any) => {
  const scrollRef = useRef(null);
  const activeCompany = useStore((state) => state.activeCompany);
  const fromDate = useStore((state) => state.fromDate);
  const toDate = useStore((state) => state.toDate);
  
  const cashFlowData = useLiveQuery(async () => {
    if (!activeCompany) return null;

    // 1. Identify Cash and Bank Ledgers
    const cashGroup = await db.groups.where('companyId').equals(activeCompany.id!).and(g => g.name === 'Cash-in-Hand').first();
    const bankGroup = await db.groups.where('companyId').equals(activeCompany.id!).and(g => g.name === 'Bank Accounts').first();
    
    const cashLedgers = await db.ledgers.where('groupId').equals(cashGroup?.id || -1).toArray();
    const bankLedgers = await db.ledgers.where('groupId').equals(bankGroup?.id || -1).toArray();
    const cashBankLedgerIds = [...cashLedgers, ...bankLedgers].map(l => l.id!);

    if (cashBankLedgerIds.length === 0) return { vouchers: [], openingBalance: 0, totalInflow: 0, totalOutflow: 0, closingBalance: 0 };

    // 2. Calculate Opening Balance
    let openingBalance = 0;
    for (const l of [...cashLedgers, ...bankLedgers]) {
      let bal = l.balanceType === 'Dr' ? l.openingBalance : -l.openingBalance;
      const preEntries = await db.voucherEntries.where('ledgerId').equals(l.id!).toArray();
      for (const e of preEntries) {
        const v = await db.vouchers.get(e.voucherId);
        if (v && !v.deleted && v.date < fromDate) {
          bal += (e.type === 'Dr' ? e.amount : -e.amount);
        }
      }
      openingBalance += bal;
    }

    // 3. Get Transactions in Period
    const entries = await db.voucherEntries.where('ledgerId').anyOf(cashBankLedgerIds).toArray();
    
    // Group by voucherId to avoid duplicate rendering for multi-leg entries
    const groupedVouchers = new Map<number, { amountDr: number; amountCr: number; particulars: Set<string> }>();
    
    for (const entry of entries) {
      const voucher = await db.vouchers.get(entry.voucherId);
      if (!voucher || voucher.deleted || voucher.date < fromDate || voucher.date > toDate) continue;

      if (!groupedVouchers.has(voucher.id!)) {
        groupedVouchers.set(voucher.id!, { amountDr: 0, amountCr: 0, particulars: new Set() });
      }

      const grp = groupedVouchers.get(voucher.id!)!;
      if (entry.type === 'Dr') grp.amountDr += entry.amount;
      else grp.amountCr += entry.amount;

      // Identify the opposite legs to display as particulars
      const allVoucherEntries = await db.voucherEntries.where('voucherId').equals(voucher.id!).toArray();
      const oppositeEntries = allVoucherEntries.filter(e => !cashBankLedgerIds.includes(e.ledgerId) && e.type !== entry.type);
      
      for (const opp of oppositeEntries) {
        const oppLedger = await db.ledgers.get(opp.ledgerId);
        if (oppLedger) grp.particulars.add(oppLedger.name);
      }
    }

    const result = [];
    let totalInflow = 0;
    let totalOutflow = 0;

    for (const [vId, grp] of groupedVouchers.entries()) {
      const voucher = await db.vouchers.get(vId);
      if (!voucher) continue;

      let particulars = Array.from(grp.particulars).join(', ');
      if (!particulars) particulars = 'Internal Transfer / Contra';
      if (grp.particulars.size > 2) particulars = 'As per details';

      // Net impact on cash/bank
      const netAmount = grp.amountDr - grp.amountCr;
      
      if (netAmount > 0) {
        totalInflow += netAmount;
        result.push({
          id: voucher.id,
          date: voucher.date,
          particulars,
          vchType: voucher.type,
          vchNo: voucher.voucherNumber,
          inflow: netAmount.toFixed(2),
          outflow: '',
          name: particulars
        });
      } else if (netAmount < 0) {
        totalOutflow += Math.abs(netAmount);
        result.push({
          id: voucher.id,
          date: voucher.date,
          particulars,
          vchType: voucher.type,
          vchNo: voucher.voucherNumber,
          inflow: '',
          outflow: Math.abs(netAmount).toFixed(2),
          name: particulars
        });
      }
    }

    const closingBalance = openingBalance + totalInflow - totalOutflow;

    return {
      vouchers: result.sort((a, b) => a.date.localeCompare(b.date)),
      openingBalance,
      totalInflow,
      totalOutflow,
      closingBalance
    };
  }, [activeCompany, fromDate, toDate]);

  useWheelNavigation(scrollRef, cashFlowData?.vouchers.length || 0, setSelectedIndex, false, () => cashFlowData?.vouchers && cashFlowData.vouchers[selectedIndex] && handleItemSelect(cashFlowData.vouchers[selectedIndex], selectedIndex));

  const shortcuts = [
    { key: 'Q', label: 'Quit' }, { key: 'Enter', label: 'Alter' }, { key: 'Space', label: 'Select' },
    { key: 'F2', label: 'Period' }
  ];

  if (!cashFlowData) {
    return (
      <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-black justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e8585]"></div>
        <div className="mt-4 font-bold text-gray-500">Loading Cash Flow Data...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px]">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10">
        <span className="w-1/3 text-left">Cash Flow</span>
        <span className="w-1/3 text-center">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="bg-white text-black flex justify-between px-2 py-1 font-bold border-b border-[#aebec8] z-10 shadow-sm">
        <span className="text-[#1e8585]">Cash Flow Summary</span>
        <span>{formatDate(fromDate)} to {formatDate(toDate)}</span>
      </div>

      <div className="flex flex-col border-b border-[#aebec8] bg-white text-black font-bold px-2 py-1 shadow-sm">
        <div className="flex w-full">
          <div className="w-[80px]">Date</div><div className="flex-1">Particulars</div><div className="w-[120px]">Vch Type</div><div className="w-[80px] text-right">Vch No.</div><div className="w-[100px] text-right">Inflow</div><div className="w-[100px] text-right">Outflow</div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-white scrollbar-thin">
        <div className="flex w-full px-2 py-[2px] text-black font-bold bg-gray-50 border-b border-gray-100">
          <div className="w-[80px]"></div>
          <div className="flex-1 italic">Opening Balance</div>
          <div className="w-[120px]"></div>
          <div className="w-[80px] text-right"></div>
          <div className="w-[100px] text-right">{cashFlowData?.openingBalance.toFixed(2)}</div>
          <div className="w-[100px] text-right"></div>
        </div>

        {cashFlowData?.vouchers.map((item: any, idx: number) => (
          <div key={idx} onClick={() => handleItemSelect(item, idx)} className={`flex w-full px-2 py-[2px] cursor-pointer ${selectedIndex === idx ? 'bg-[#1e8585] text-white font-bold active-row' : 'text-black'}`}>
            <div className="w-[80px]">{formatDate(item.date)}</div>
            <div className="flex-1 italic font-semibold">{item.particulars}</div>
            <div className="w-[120px] font-semibold">{item.vchType}</div>
            <div className="w-[80px] text-right">{item.vchNo}</div>
            <div className="w-[100px] text-right font-semibold text-emerald-600">{item.inflow}</div>
            <div className="w-[100px] text-right font-semibold text-red-600">{item.outflow}</div>
          </div>
        ))}
        
        {cashFlowData?.vouchers.length === 0 && (
          <div className="p-4 text-center text-gray-500 italic">No cash transactions found in this period.</div>
        )}
      </div>

      <div className="flex flex-col border-t border-[#aebec8] bg-[#f9fafb] px-2 py-2 gap-1">
        <div className="flex justify-between font-bold text-black">
          <div className="flex-1 text-right pr-4">Total Inflow:</div>
          <div className="w-[100px] text-right text-emerald-600">{cashFlowData?.totalInflow.toFixed(2)}</div>
          <div className="w-[100px]"></div>
        </div>
        <div className="flex justify-between font-bold text-black">
          <div className="flex-1 text-right pr-4">Total Outflow:</div>
          <div className="w-[100px]"></div>
          <div className="w-[100px] text-right text-red-600">{cashFlowData?.totalOutflow.toFixed(2)}</div>
        </div>
        <div className="flex justify-between font-bold text-[14px] text-[#1e8585] border-t border-gray-300 pt-1">
          <div className="flex-1 text-right pr-4 uppercase">Closing Balance:</div>
          <div className="w-[200px] text-right pr-2">{cashFlowData?.closingBalance.toFixed(2)}</div>
        </div>
      </div>

      <div className="flex border-t border-[#aebec8] bg-white h-[22px] w-full shadow-inner mt-auto">
        {shortcuts.map((sc, i) => (
          <div 
            key={i} 
            className={`flex-1 border-r border-[#aebec8] flex justify-between items-center px-1 cursor-pointer hover:bg-gray-100`}
            onClick={() => {
              if (sc.label === 'Quit') handleItemSelect({ name: 'Quit' });
            }}
          >
            <div className="flex items-center space-x-[2px] whitespace-nowrap">
              <span className={`text-[#2a62a3] font-bold underline decoration-[#2a62a3] underline-offset-2`}>{sc.key}</span>
              <span className="text-[#2a62a3] font-bold">:</span><span className="text-black leading-none">{sc.label}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
